<script lang="ts">
  import { formatIST } from '$lib/utils/dates';
  import { storedColor } from '$lib/utils/colors';
  import { onDestroy } from 'svelte';
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { Mail, Phone, ArrowRight, Check, AlertTriangle, UserPlus, Wrench } from 'lucide-svelte';
  import { leadsApi } from '$lib/api/leads';
  import { peopleApi } from '$lib/api/crm';
  import { session } from '$lib/state/session.svelte';
  import { apiFetch, getErrorMessage } from '$lib/api/client';
  import { route } from '$lib/state/route.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import CommentThread from '$lib/components/ui/CommentThread.svelte';
  import RecordRead from '../RecordRead.svelte';
  import type { AssignableUser, Lead, LeadStatus } from '$lib/types';

  // `lead` is owned by the parent (LeadsPage): every mutation below reports
  // its result back via onUpdated so the parent can refresh it, rather than
  // this component keeping its own stale copy.
  const {
    lead: suppliedLead,
    onNavigate,
    onChanged,
    onUpdated: notifyUpdated
  }: {
    lead: Lead;
    onNavigate: (tab: string) => void;
    onChanged: () => void;
    onUpdated: (updated: Lead) => void;
  } = $props();

  const queryClient = useQueryClient();
  let confirmedLeadGet = $state<{ id: number; userId?: number } | null>(null);
  const detailQuery = createQuery(() => ({
    queryKey: ['lead-displayed-detail', session.me?.user.id, suppliedLead.id],
    queryFn: async () => {
      const requestedId = suppliedLead.id, userId = session.me?.user.id;
      const row = await leadsApi.get(requestedId);
      if (row.id === requestedId && suppliedLead.id === requestedId && session.me?.user.id === userId) confirmedLeadGet = { id: row.id, userId };
      return row;
    },
    enabled: session.me !== null && route.current.tab === 'leads' && route.current.id === suppliedLead.id
  }));
  const lead = $derived(detailQuery.data?.id === suppliedLead.id && !(Date.parse(suppliedLead.updated_at) > Date.parse(detailQuery.data.updated_at)) ? detailQuery.data : suppliedLead);
  function onUpdated(updated: Lead) {
    queryClient.setQueryData(['lead-displayed-detail', session.me?.user.id, updated.id], updated);
    notifyUpdated(updated);
  }

  type LeadSubmission = { leadId: number; generation: number; body: Record<string, unknown> };
  let active = true;
  onDestroy(() => { active = false; });
  let issueGeneration = $state(0);
  let convertGeneration = $state(0);
  function isCurrentLead(id: number) {
    return active && lead.id === id && route.current.tab === 'leads' && route.current.id === id;
  }
  function matchesIssue(submission: LeadSubmission) { return isCurrentLead(submission.leadId) && showIssue && issueGeneration === submission.generation; }
  function matchesConvert(submission: LeadSubmission) { return isCurrentLead(submission.leadId) && showConvert && convertGeneration === submission.generation; }
  function closeIssue() { issueGeneration++; showIssue = false; }
  function closeConvert() { convertGeneration++; showConvert = false; }

  const issueTeams = $derived(session.me?.issue_verticals ?? session.me?.verticals ?? []);
  let showIssue = $state(false);
  let success = $state('');
  let issueForm = $state({ vertical_id: undefined as number | undefined, assignee_user_id: null as number | null, next_action: '', next_action_date: '' });
  const assignees = createQuery(() => ({
    queryKey: ['lead-handoff-assignees', issueForm.vertical_id],
    queryFn: () => apiFetch<{ results: AssignableUser[] }>(`/users/assignable?vertical_id=${issueForm.vertical_id}`),
    enabled: showIssue && issueForm.vertical_id != null
  }));
  const issueMutation = createMutation(() => ({
    mutationFn: (submission: LeadSubmission) => leadsApi.issue(submission.leadId, submission.body),
    onSuccess: ({ lead: updated }, submission) => {
      onUpdated(updated); onChanged();
      if (!matchesIssue(submission)) return;
      closeIssue(); success = 'Issue created and handed over. The original lead remains in history.';
    },
    onError: (err: unknown, submission) => { if (matchesIssue(submission)) actionError = getErrorMessage(err); }
  }));
  function logAsIssue() {
    issueGeneration++;
    issueForm = { vertical_id: issueTeams.find(v => v.id === lead.vertical_id)?.id ?? issueTeams[0]?.id, assignee_user_id: null, next_action: '', next_action_date: '' };
    actionError = ''; showIssue = true;
  }

  let actionError = $state('');
  async function openContact() {
    const leadId = lead.id;
    try { const person = await peopleApi.get(lead.person_id!); if (!isCurrentLead(leadId)) return; session.requestEdit('people', person); onNavigate('people'); }
    catch (err) { if (isCurrentLead(leadId)) actionError = getErrorMessage(err); }
  }
  let showConvert = $state(false);

  const downloadOnly = $derived(lead.purpose === 'guide_download' && !lead.contact_requested);
  const statuses = $derived<LeadStatus[]>(lead.person_id || lead.issue_id ? ['converted'] : downloadOnly ? ['download', 'discarded'] : ['new', 'contacted', 'discarded']);

  const STATUS_STYLE: Record<LeadStatus, { bg: string; fg: string; label: string }> = {
    download: { bg: '#E3F0F1', fg: '#226A73', label: 'Download only' },
    new: { bg: '#ECF8CC', fg: '#5c7a00', label: 'New' },
    contacted: { bg: '#E3F0F1', fg: '#226A73', label: 'Contacted' },
    converted: { bg: '#E6E9EE', fg: '#10223A', label: 'Converted' },
    discarded: { bg: '#F1EDEC', fg: '#999D9E', label: 'Discarded' }
  };

  // ponytail: category colors have no backend field of their own — cycle a
  // small deterministic palette by hashing the category string, same trick
  // used for vertical dots in the sidebar.
  const CAT_PALETTE = [
    { bg: '#E3F0F1', fg: '#226A73' },
    { bg: '#ECF8CC', fg: '#5c7a00' },
    { bg: '#FBF0DC', fg: '#8a5c00' },
    { bg: '#E4EEEF', fg: '#5F9CA3' }
  ];
  function categoryStyle(category: string) {
    if (!category) return { bg: '#F1F0EF', fg: '#666666' };
    const fg = storedColor(session.me?.verticals.find(v => v.id === lead.vertical_id)?.settings?.category_colors?.[category]);
    return { bg: `${fg}1F`, fg };
  }

  const initials = $derived(
    (lead.name || lead.email || '?')
      .trim()
      .split(/\s+/)
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase()
  );

  function blankConvertForm() {
    return {
      vertical_id: undefined as number | undefined,
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      relationship_type: '',
      next_action: '',
      next_action_date: '',
      company_name: '',
      company_city: '',
      company_phone: '',
      company_email: '',
      company_website: ''
    };
  }
  let convertForm = $state(blankConvertForm());

  function openConvert() {
    convertGeneration++;
    const [first, ...rest] = lead.name.trim().split(/\s+/).filter(Boolean);
    convertForm = {
      ...blankConvertForm(),
      vertical_id: lead.vertical_id ?? session.me?.verticals[0]?.id,
      first_name: first ?? '',
      last_name: rest.join(' '),
      email: lead.email,
      phone: lead.phone
    };
    actionError = '';
    showConvert = true;
  }

  const statusMutation = createMutation(() => ({
    mutationFn: (submission: LeadSubmission) => leadsApi.update(submission.leadId, submission.body),
    onSuccess: (updated) => {
      onUpdated(updated);
      onChanged();
    },
    onError: (err: unknown, submission) => { if (isCurrentLead(submission.leadId)) actionError = getErrorMessage(err); }
  }));

  const resendMutation = createMutation(() => ({
    mutationFn: (submission: LeadSubmission) => leadsApi.resend(submission.leadId),
    onSuccess: (updated) => {
      onUpdated(updated);
      onChanged();
    },
    onError: (err: unknown, submission) => { if (isCurrentLead(submission.leadId)) actionError = getErrorMessage(err); }
  }));

  const convertMutation = createMutation(() => ({
    mutationFn: (submission: LeadSubmission) => leadsApi.convert(submission.leadId, submission.body),
    onSuccess: ({ lead: updated }, submission) => {
      onUpdated(updated);
      onChanged();
      if (!matchesConvert(submission)) return;
      closeConvert();
      success = 'Contact saved with the original enquiry in its history.';
    },
    onError: (err: unknown, submission) => { if (matchesConvert(submission)) actionError = getErrorMessage(err); }
  }));

  function submitStatus(body: Record<string, unknown>) {
    if (!isCurrentLead(lead.id)) return;
    statusMutation.mutate({ leadId: lead.id, generation: 0, body: { ...body } });
  }

  function submitConvert(e: SubmitEvent) {
    e.preventDefault();
    if (!isCurrentLead(lead.id)) return;
    actionError = '';
    convertMutation.mutate({ leadId: lead.id, generation: convertGeneration, body: { ...convertForm } });
  }

  const metaRows = $derived([
    { k: 'Request type', v: lead.purpose === 'guide_download' ? 'Guide download' : 'Enquiry' },
    { k: 'Source', v: lead.source || '(none)' },
    { k: 'Source page', v: lead.page || '(none)' },
    { k: 'Referrer', v: lead.referrer || '(none)' },
    { k: 'UTM source', v: lead.utm_source || '-' },
    { k: 'UTM medium', v: lead.utm_medium || '-' },
    { k: 'UTM campaign', v: lead.utm_campaign || '-' },
    { k: 'Submitted', v: formatIST(lead.created_at) },
    { k: 'IP address', v: lead.ip_address || '(none)' }
  ]);
</script>

<div class="mx-auto max-w-[760px] px-5 py-6 md:px-8 md:py-[26px]">
  <RecordRead kind="leads" recordId={suppliedLead.id} dataId={detailQuery.data?.id} ready={detailQuery.isSuccess && !detailQuery.isFetching && confirmedLeadGet?.id === suppliedLead.id && confirmedLeadGet.userId === session.me?.user.id && route.current.tab === 'leads' && route.current.id === suppliedLead.id} />
  {#if detailQuery.isPending}<p role="status" class="mb-3 text-sm text-dark-gray">Confirming lead details…</p>
  {:else if detailQuery.isError}<p role="alert" class="mb-3 text-sm text-red-700">Unable to refresh current lead details. No read confirmation was sent for this failed load. <button type="button" class="underline" onclick={() => detailQuery.refetch()}>Retry</button></p>{/if}
  {#if lead.spam}
    <div class="tym-error-state mb-4">Flagged as spam via the honeypot field. Never routed or notified.</div>
  {/if}

  <div class="mb-[22px] flex items-start gap-4">
    <span
      class="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full font-heading text-[18px] font-bold"
      style="background:#E3F0F1; color:#226A73"
    >
      {initials}
    </span>
    <div class="min-w-0 flex-1">
      <h2 class="font-heading text-[22px] font-bold leading-tight tracking-tight text-navy">
        {lead.name || lead.email || '(no name)'}
      </h2>
      <div class="mt-1.5 flex flex-wrap gap-3.5 text-[13px] text-[#666]">
        {#if lead.email}
          <span class="inline-flex items-center gap-1.5"><Mail class="h-3.5 w-3.5 text-dark-gray" />{lead.email}</span>
        {/if}
        {#if lead.phone}
          <span class="inline-flex items-center gap-1.5"><Phone class="h-3.5 w-3.5 text-dark-gray" />{lead.phone}</span>
        {/if}
      </div>
    </div>
    <span class="tym-badge shrink-0 !rounded-full !px-3 !py-1.5 !text-[11.5px]" style="background:{STATUS_STYLE[lead.status].bg}; color:{STATUS_STYLE[lead.status].fg}">
      {STATUS_STYLE[lead.status].label}
    </span>
  </div>

  <div class="mb-6 flex flex-wrap gap-2.5">
    {#if !lead.person_id && !lead.spam && !downloadOnly}
      <button type="button" class="btn-primary !h-[42px]" onclick={openConvert}>
        <ArrowRight class="h-[17px] w-[17px]" />
        Save as contact
      </button>
    {/if}
    {#if !lead.person_id && !lead.issue_id && lead.status !== 'contacted' && !lead.spam && !downloadOnly}
      <button
        type="button"
        class="btn-secondary !h-[42px]"
        disabled={statusMutation.isPending}
        onclick={() => submitStatus({ status: 'contacted' })}
      >
        Mark contacted
      </button>
    {/if}
    {#if !lead.person_id && !lead.issue_id && lead.status !== 'discarded'}
      <button
        type="button"
        class="btn-danger !h-[42px]"
        disabled={statusMutation.isPending}
        onclick={() => submitStatus({ status: 'discarded' })}
      >
        Discard
      </button>
    {/if}
    {#if !lead.issue_id && !lead.spam && !downloadOnly}
      <button type="button" class="btn-secondary !h-[42px]" onclick={logAsIssue}>
        <Wrench class="h-[17px] w-[17px]" />
        Create issue &amp; hand over
      </button>
    {/if}
  </div>

  {#if lead.person_id}<button type="button" class="btn-secondary mb-3" onclick={openContact}>Open saved contact</button>{/if}
  {#if lead.issue_id}<button type="button" class="btn-secondary mb-3" onclick={() => route.push({ tab: 'issues', id: lead.issue_id! })}>Open handed-over issue</button>{/if}
  {#if success}<p class="mb-3 text-sm text-toyama-teal" role="status">{success}</p>{/if}

  {#if lead.purpose === 'guide_download'}
    <div class="tym-card mb-4 border-l-4 border-toyama-teal px-5 py-4">
      <h3 class="font-heading font-semibold text-navy">Guide download · {lead.guide_id === 'lighting-essentials' ? 'Lighting Essentials' : 'Home Lighting Guide'}</h3>
      <p class="mt-2 text-sm">Audience: {lead.guide_audience === 'homeowner' ? 'Homeowner' : 'Architect / interior designer'}.</p>
      <p class="mt-2 text-sm font-semibold">{downloadOnly ? 'Download only — no permission to call, send sales email or WhatsApp. Sales conversion and notification are disabled.' : 'Project follow-up explicitly requested by email only. No permission to call or WhatsApp.'}</p>
      <p class="mt-2 text-xs">Permission belongs to this request. It does not change permissions on earlier enquiries or existing contacts.</p>
    </div>
  {/if}
  {#if lead.message}
    <div class="tym-card mb-[18px] px-5 py-[18px]">
      <div class="mb-2 text-[11px] font-semibold uppercase tracking-[.08em] text-toyama-teal">Message</div>
      <p class="m-0 text-[14px] font-light leading-[1.6] text-toyama-heavy-carbon">{lead.message}</p>
    </div>
  {/if}

  <div class="mb-[18px] grid grid-cols-1 gap-3.5 sm:grid-cols-2">
    <div class="tym-card px-[18px] py-4">
      <div class="mb-2.5 text-[11px] font-semibold uppercase tracking-[.08em] text-dark-gray">Routing</div>
      <div class="flex flex-wrap items-center gap-2.5">
        {#if lead.category}
          {@const cs = categoryStyle(lead.category)}
          <span class="tym-badge" style="background:{cs.bg}; color:{cs.fg}">{lead.category}</span>
        {:else}
          <span class="text-[13px] text-dark-gray">(no category)</span>
        {/if}
        {#if lead.vertical_id}
          <ArrowRight class="h-4 w-4 text-dark-gray" />
          <span class="inline-flex items-center gap-1.5 text-[13px] font-semibold text-navy">
            <span class="h-2 w-2 rounded-full bg-toyama-teal"></span>
            {session.me?.verticals.find((v) => v.id === lead.vertical_id)?.name ?? `#${lead.vertical_id}`}
          </span>
        {:else}
          <span class="text-[13px] text-dark-gray">Email-only &middot; no CRM record</span>
        {/if}
      </div>
    </div>
    <div class="tym-card px-[18px] py-4">
      <div class="mb-2.5 text-[11px] font-semibold uppercase tracking-[.08em] text-dark-gray">Notification</div>
      <div class="flex items-center justify-between gap-2">
        <span
          class="inline-flex items-center gap-1.5 text-[13px] font-semibold"
          style="color:{lead.notify_status === 'sent' ? '#5c7a00' : lead.notify_status === 'failed' ? 'var(--tym-danger)' : 'var(--tym-dark-gray)'}"
        >
          {#if lead.notify_status === 'sent'}
            <Check class="h-[15px] w-[15px]" />
          {:else if lead.notify_status === 'failed'}
            <AlertTriangle class="h-[15px] w-[15px]" />
          {/if}
          {lead.notify_status === 'sent'
            ? 'Email sent'
            : lead.notify_status === 'failed'
              ? 'Delivery failed'
              : lead.notify_status}
        </span>
        {#if lead.source !== 'manual' && !lead.spam && !downloadOnly}
          <button
            type="button"
            class="text-[12px] font-semibold text-toyama-teal underline decoration-1 underline-offset-2 disabled:opacity-50"
            onclick={() => resendMutation.mutate({ leadId: lead.id, generation: 0, body: {} })}
            disabled={resendMutation.isPending}
          >
            {resendMutation.isPending ? 'Resending...' : 'Resend'}
          </button>
        {/if}
      </div>
      {#if lead.notify_error}
        <p class="mt-1.5 text-[11.5px] text-dark-gray">{lead.notify_error}</p>
      {/if}
    </div>
  </div>

  <div class="tym-card mb-[18px] px-5">
    {#each metaRows as m (m.k)}
      <div class="flex gap-4 border-t border-[#F0F0F0] py-[11px] first:border-t-0">
        <span class="w-[120px] shrink-0 text-[12px] font-medium text-dark-gray">{m.k}</span>
        <span class="min-w-0 flex-1 break-all font-heading text-[13px] text-toyama-heavy-carbon">{m.v}</span>
      </div>
    {/each}
  </div>

  <div class="tym-card px-[18px] py-4">
    <div class="mb-2.5 text-[11px] font-semibold uppercase tracking-[.08em] text-dark-gray">Manage</div>
    <div class="flex flex-wrap items-center gap-3">
      <select
        class="input w-auto"
        value={lead.status}
        onchange={(e) => submitStatus({ status: (e.currentTarget as HTMLSelectElement).value })}
      >
        {#each statuses as s (s)}
          <option value={s}>{STATUS_STYLE[s].label}</option>
        {/each}
      </select>
      <label class="flex items-center gap-2 text-sm text-toyama-heavy-carbon">
        <input
          type="checkbox"
          checked={lead.spam}
          onchange={(e) => submitStatus({ spam: (e.currentTarget as HTMLInputElement).checked })}
        />
        Spam
      </label>
    </div>
  </div>

  {#if actionError}<p class="mt-3 text-sm text-red-600">{actionError}</p>{/if}

  <div class="mt-[18px]">
    <CommentThread entityType="lead" entityId={lead.id} />
  </div>
</div>

{#if showConvert}
  <Modal title="Save as contact" onClose={closeConvert}>
    <div class="-mt-2 mb-4 flex items-center gap-3">
      <span
        class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
        style="background:#ECF8CC; color:#5c7a00"
      >
        <UserPlus class="h-[22px] w-[22px]" />
      </span>
      <p class="m-0 text-[12.5px] text-dark-gray">
        Creates a <strong class="font-semibold text-toyama-heavy-carbon">Person</strong> from
        <strong class="font-semibold text-toyama-heavy-carbon">{lead.name || lead.email}</strong>, links an optional
        company, and logs the original message as a note activity.
      </p>
    </div>
    <form class="space-y-4" oninput={() => { convertGeneration++; actionError = ''; }} onsubmit={submitConvert}>
      <label class="label" for="lead-relationship">Relationship</label>
      <select id="lead-relationship" class="input" bind:value={convertForm.relationship_type}>
        {#each ['', 'architect', 'interior_designer', 'lighting_vendor', 'lighting_consultant', 'pmc', 'electrical_contractor', 'homeowner', 'other'] as value}<option {value}>{value.replaceAll('_', ' ') || 'Not specified'}</option>{/each}
      </select>
      <label class="label" for="contact-next">Next action</label><input id="contact-next" class="input" bind:value={convertForm.next_action} />
      <label class="label" for="contact-next-date">Next action date</label><input id="contact-next-date" class="input" type="date" bind:value={convertForm.next_action_date} />
      {#if lead.vertical_id === null}
        <div>
          <label class="label" for="convert-vertical">Vertical</label>
          <select id="convert-vertical" class="input" bind:value={convertForm.vertical_id}>
            {#each session.me?.verticals ?? [] as v (v.id)}
              <option value={v.id}>{v.name}</option>
            {/each}
          </select>
        </div>
      {/if}
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label class="label" for="convert-first">First name</label>
          <input id="convert-first" class="input" bind:value={convertForm.first_name} />
        </div>
        <div>
          <label class="label" for="convert-last">Last name</label>
          <input id="convert-last" class="input" bind:value={convertForm.last_name} placeholder="(leave blank if single-word name)" />
        </div>
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label class="label" for="convert-email">Email</label>
          <input id="convert-email" class="input" bind:value={convertForm.email} />
        </div>
        <div>
          <label class="label" for="convert-phone">Phone</label>
          <input id="convert-phone" class="input" bind:value={convertForm.phone} />
        </div>
      </div>
      <p class="text-xs text-dark-gray">Only fill in a company if you know one — it is never guessed automatically.</p>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label class="label" for="convert-company">Company name (optional)</label>
          <input id="convert-company" class="input" bind:value={convertForm.company_name} />
        </div>
        <div>
          <label class="label" for="convert-company-city">Company city</label>
          <input id="convert-company-city" class="input" bind:value={convertForm.company_city} />
        </div>
      </div>
      {#if actionError}<p class="text-sm text-red-600">{actionError}</p>{/if}
      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={closeConvert}>Cancel</button>
        <button type="submit" class="btn-primary" disabled={convertMutation.isPending}>
          {convertMutation.isPending ? 'Saving...' : 'Save as contact'}
        </button>
      </div>
    </form>
  </Modal>
{/if}

{#if showIssue}
  <Modal title="Create issue & hand over" onClose={closeIssue}>
    <form class="space-y-4" oninput={() => { issueGeneration++; actionError = ''; }} onsubmit={(e) => { e.preventDefault(); if (!isCurrentLead(lead.id)) return; actionError = ''; issueMutation.mutate({ leadId: lead.id, generation: issueGeneration, body: { ...issueForm } }); }}>
      <p class="text-sm text-dark-gray">Copies the customer details and original enquiry to the support team. The lead and any saved contact are preserved.</p>
      <label class="label" for="handoff-team">Support team</label>
      <select id="handoff-team" class="input" required bind:value={issueForm.vertical_id} onchange={() => (issueForm.assignee_user_id = null)}>{#each issueTeams as v (v.id)}<option value={v.id}>{v.name}</option>{/each}</select>
      <label class="label" for="handoff-assignee">Assign to</label>
      <select id="handoff-assignee" class="input" bind:value={issueForm.assignee_user_id}><option value={null}>Unassigned</option>{#each assignees.data?.results ?? [] as u (u.id)}<option value={u.id}>{u.name}</option>{/each}</select>
      {#if assignees.isError}<p class="text-sm text-red-600">{getErrorMessage(assignees.error)}</p>{/if}
      <label class="label" for="handoff-next">Next action</label><input id="handoff-next" class="input" bind:value={issueForm.next_action} />
      <label class="label" for="handoff-date">Next action date</label><input id="handoff-date" class="input" type="date" bind:value={issueForm.next_action_date} />
      {#if actionError}<p class="text-sm text-red-600">{actionError}</p>{/if}
      <button type="submit" class="btn-primary" disabled={issueMutation.isPending || !issueForm.vertical_id}>{issueMutation.isPending ? 'Creating…' : 'Create issue & hand over'}</button>
    </form>
  </Modal>
{/if}
